import { type CSSProperties } from 'react';

import {
  AspectRatio,
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  useBreakpointValue,
  useColorModeValue,
  usePrefersReducedMotion,
} from '@ecoinvent/ui';
import { BiX } from 'react-icons/bi';

import { COLORS } from './constants';
import { SUPPLY_CHAIN_INFO_FRAME_RATIO, SUPPLY_CHAIN_INFO_STEPS } from './supplyChainInfoSteps';

const BODY_ID = 'supply-chain-info-card-body';
const CARD_WIDTH = '320px';
const ICON_SIZE = '40px';

const FILL: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover' };

type InfoVideoProps = {
  src: string;
  isStatic: boolean;
};

// The step text carries all the meaning, so both videos are decorative.
const InfoVideo = ({ src, isStatic }: InfoVideoProps) =>
  isStatic ? (
    // `preload="none"` paints nothing until playback starts; with autoplay off we ask for the first frame instead.
    <video src={`${src}#t=0.001`} style={FILL} muted playsInline preload="metadata" aria-hidden="true" />
  ) : (
    <video src={src} style={FILL} muted autoPlay loop playsInline preload="none" aria-hidden="true" />
  );

type Props = {
  stepIndex: number;
  onNext: () => void;
  onClose: () => void;
};

const SupplyChainInfoCard = ({ stepIndex, onNext, onClose }: Props) => {
  const isWideEnough = useBreakpointValue({ base: false, lg: true }) ?? false;
  const prefersReducedMotion = usePrefersReducedMotion();

  const bg = useColorModeValue('white', COLORS.darkSurface);
  const borderColor = useColorModeValue(COLORS.borderLight, COLORS.buttonOutlinedBorderDark);
  // A black `xl` shadow is imperceptible on the dark canvas, which is why the other overlays swap it too.
  const shadow = useColorModeValue('xl', 'dark-lg');
  const bodyColor = useColorModeValue('gray.600', COLORS.grayDim);
  const mediaBg = useColorModeValue('gray.100', 'gray.700');
  const dotActiveBg = useColorModeValue('gray.800', 'white');
  const dotInactiveBg = useColorModeValue('gray.200', 'gray.600');

  const totalSteps = SUPPLY_CHAIN_INFO_STEPS.length;
  // `.at()` wraps on a negative index, so the range check cannot be left to it.
  const step = stepIndex >= 0 ? SUPPLY_CHAIN_INFO_STEPS.at(stepIndex) : undefined;
  const isLastStep = stepIndex === totalSteps - 1;
  const frameSrc = useColorModeValue(step?.frame.light, step?.frame.dark);
  const iconSrc = useColorModeValue(step?.icon.light, step?.icon.dark);

  if (!isWideEnough || !step || !frameSrc || !iconSrc) return null;

  return (
    // Paints above the graph: the `.react-flow` wrapper is its own `z-index: 0` stacking context.
    <Box
      role="region"
      aria-label="Graph Explorer onboarding"
      aria-describedby={BODY_ID}
      position="absolute"
      bottom={4}
      left={4}
      zIndex={10}
      w={CARD_WIDTH}
      bg={bg}
      borderRadius="lg"
      boxShadow={shadow}
      borderWidth="0.5px"
      borderColor={borderColor}
      p={4}
    >
      <IconButton
        position="absolute"
        top={2}
        right={2}
        aria-label="Close"
        icon={<Icon as={BiX} />}
        size="xs"
        fontSize="lg"
        variant="ghost"
        colorScheme="gray"
        onClick={onClose}
      />
      <Stack spacing={3}>
        <Heading as="h4" size="sm" textAlign="center">
          Graph Explorer
        </Heading>
        <AspectRatio ratio={SUPPLY_CHAIN_INFO_FRAME_RATIO} bg={mediaBg} borderRadius="md" overflow="hidden">
          <InfoVideo key={frameSrc} src={frameSrc} isStatic={prefersReducedMotion} />
        </AspectRatio>
        <HStack
          spacing={2}
          justify="center"
          role="progressbar"
          aria-valuenow={stepIndex + 1}
          aria-valuemin={1}
          aria-valuemax={totalSteps}
          aria-label={`Step ${stepIndex + 1} of ${totalSteps}`}
        >
          {SUPPLY_CHAIN_INFO_STEPS.map((s, i) => (
            <Box key={s.title} boxSize="6px" borderRadius="full" bg={i === stepIndex ? dotActiveBg : dotInactiveBg} />
          ))}
        </HStack>
        <HStack spacing={3} align="flex-start">
          <Box boxSize={ICON_SIZE} flexShrink={0} borderRadius="full" overflow="hidden" bg={mediaBg}>
            <InfoVideo key={iconSrc} src={iconSrc} isStatic={prefersReducedMotion} />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              {step.title}
            </Text>
            <Text id={BODY_ID} fontSize="sm" color={bodyColor}>
              {step.description}
            </Text>
          </Box>
        </HStack>
        <Button colorScheme="green" size="sm" w="full" onClick={isLastStep ? onClose : onNext}>
          {isLastStep ? 'Done' : 'Next'}
        </Button>
      </Stack>
    </Box>
  );
};

export default SupplyChainInfoCard;
